/**
 * @fileoverview WebSocket client for the Personal Quick Reaction Admin Operator Panel ("Reacción Rápida Personal" - Tile ID 45).
 * Connects to the platform's real-time communication channel as superadmin (`?tipo=superadmin`) to receive
 * live plays, leaderboard shifts, configuration updates, and emit operator control commands.
 * 
 * @module utilities/WS/Admin/PersonalQuickReactionWS
 */

let personalQuickReactionAdminWebSocket = null;
let isPersonalQuickReactionAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 20;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the Personal Quick Reaction admin panel.
 * 
 * @function ConnectPersonalQuickReactionAdminWS
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a formatted event message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectPersonalQuickReactionAdminWS = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (personalQuickReactionAdminWebSocket || isPersonalQuickReactionAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const rawWssUrl = process.env.REACT_APP_API_URL_WSS || 'wss://localhost:5001/ws';
            const webSocketEndpointUrl = `${rawWssUrl}?tipo=superadmin`;
            
            personalQuickReactionAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            personalQuickReactionAdminWebSocket.onopen = () => {
                isPersonalQuickReactionAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');

                // Request initial snapshot upon successful connection
                RequestState();
            };

            personalQuickReactionAdminWebSocket.onclose = () => {
                isPersonalQuickReactionAdminConnected = false;
                personalQuickReactionAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            personalQuickReactionAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            personalQuickReactionAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    
                    // Dispatch all messages and specific Personal Quick Reaction events
                    // Events: personalQuickReaction.newPlay, personalQuickReaction.configUpdated,
                    //         personalQuickReaction.overlayUpdated, personalQuickReaction.reset
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Personal Quick Reaction admin WebSocket connection.
 * 
 * @function SendPersonalQuickReactionAdminMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendPersonalQuickReactionAdminMessage = (payloadObject) => {
    try {
        if (personalQuickReactionAdminWebSocket && personalQuickReactionAdminWebSocket.readyState === WebSocket.OPEN) {
            personalQuickReactionAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        console.warn('Error al transmitir mensaje WebSocket Admin PersonalQuickReaction:', sendError);
        return false;
    }
};

/**
 * Closes and cleans up the Personal Quick Reaction admin WebSocket connection.
 * 
 * @function DisconnectPersonalQuickReactionAdminWS
 */
export const DisconnectPersonalQuickReactionAdminWS = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (personalQuickReactionAdminWebSocket) {
            personalQuickReactionAdminWebSocket.onopen = null;
            personalQuickReactionAdminWebSocket.onclose = null;
            personalQuickReactionAdminWebSocket.onerror = null;
            personalQuickReactionAdminWebSocket.onmessage = null;

            if (personalQuickReactionAdminWebSocket.readyState === WebSocket.OPEN) {
                personalQuickReactionAdminWebSocket.close();
            } else if (personalQuickReactionAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = personalQuickReactionAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            personalQuickReactionAdminWebSocket = null;
            isPersonalQuickReactionAdminConnected = false;
            connectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};

// =============================================================================
// OPERATOR COMMAND ACTION HELPERS
// =============================================================================

/**
 * Requests the current synchronized game state snapshot from the server.
 * 
 * @function RequestState
 * @param {string} [roomId=null] - Optional room GUID.
 * @returns {boolean} True if sent successfully.
 */
export const RequestState = (roomId = null) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_request_state',
        roomId: roomId || undefined,
        data: { roomId: roomId || undefined }
    });
};

/**
 * Emits command to project the promotional cover screen.
 * 
 * @function ShowCover
 * @param {string} [roomId=null] - Room GUID.
 * @param {string|number|null} [themeId=null] - Visual theme identifier.
 * @returns {boolean} True if sent successfully.
 */
export const ShowCover = (roomId = null, themeId = null) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_show_cover',
        roomId: roomId || undefined,
        themeId: themeId || undefined,
        data: { roomId, themeId }
    });
};

/**
 * Emits command to project the game arena screen.
 * 
 * @function ShowGame
 * @param {string} [roomId=null] - Room GUID.
 * @param {string|number|null} [themeId=null] - Visual theme identifier.
 * @returns {boolean} True if sent successfully.
 */
export const ShowGame = (roomId = null, themeId = null) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_show_game',
        roomId: roomId || undefined,
        themeId: themeId || undefined,
        data: { roomId, themeId }
    });
};

/**
 * Emits command to reset room or notify clients of a reset event.
 * 
 * @function ResetMatch
 * @param {string} [roomId=null] - Room GUID.
 * @param {boolean} [preserveIdentity=true] - Preserve connected sessions.
 * @returns {boolean} True if sent successfully.
 */
export const ResetMatch = (roomId = null, preserveIdentity = true) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_reset',
        roomId: roomId || undefined,
        preserveIdentity: Boolean(preserveIdentity),
        data: { roomId, preserveIdentity: Boolean(preserveIdentity) }
    });
};

/**
 * Emits updated game configuration in real time via WebSocket.
 * 
 * @function SaveConfigWS
 * @param {Object} configData - Configuration parameters.
 * @returns {boolean} True if sent successfully.
 */
export const SaveConfigWS = (configData) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_save_config',
        ...configData,
        data: configData
    });
};

/**
 * Emits updated Live Overlay configuration in real time via WebSocket.
 * 
 * @function UpdateOverlayWS
 * @param {Object} overlayData - Overlay parameters (mode, qrLayout, isVisible, offsetY).
 * @returns {boolean} True if sent successfully.
 */
export const UpdateOverlayWS = (overlayData) => {
    return SendPersonalQuickReactionAdminMessage({
        type: 'personal_quick_reaction_update_overlay',
        ...overlayData,
        data: overlayData
    });
};

// =============================================================================
// ALIASES FOR COMPATIBILITY
// =============================================================================

export const ConnectWebSocketForPersonalQuickReactionAdmin = ConnectPersonalQuickReactionAdminWS;
export const DisconnectWebSocketForPersonalQuickReactionAdmin = DisconnectPersonalQuickReactionAdminWS;
export const RequestPersonalQuickReactionAdminState = RequestState;
export const CloseWebSocket = DisconnectPersonalQuickReactionAdminWS;

const PersonalQuickReactionAdminWS = {
    ConnectPersonalQuickReactionAdminWS,
    DisconnectPersonalQuickReactionAdminWS,
    SendPersonalQuickReactionAdminMessage,
    RequestState,
    ShowCover,
    ShowGame,
    ResetMatch,
    SaveConfigWS,
    UpdateOverlayWS,
    ConnectWebSocketForPersonalQuickReactionAdmin,
    DisconnectWebSocketForPersonalQuickReactionAdmin,
    RequestPersonalQuickReactionAdminState,
    CloseWebSocket
};

export default PersonalQuickReactionAdminWS;

